import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient } from '../../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/migration/migrationService.js` (AngularJS `MigrationService`)
 * + the fetch/action logic of `app/migration/migrationController.js`
 * (`migrationController`, the controller for `migration.html`),
 * `app/migration/migrationDetailController.js` and
 * `app/migration/migrationJsonController.js` to TanStack Query hooks.
 *
 * Task 021 (listing screen) added `useMigrationSearchMutation`,
 * `useDeleteMigrationMutation` and `useFireMigrationMutation` below. Task 022
 * (detail/JSON screens) adds the remaining three `migrationService.js`
 * methods — `getMigrationDetail`, `getMigrationEntityJson` and `editJson` —
 * as `useMigrationDetailQuery`, `useMigrationEntityJsonQuery` and
 * `useEditMigrationJsonMutation` further down this same file, per the task
 * brief's "reutilizar/estender `useMigrationQuery`". Unlike the task-021
 * hooks (all `useMutation`, matching the legacy controller's
 * imperative/callback-driven calls with no natural query key), the two new
 * read hooks use `useQuery`: both `migrationDetailController.initView` and
 * `migrationJsonController.initView` fetch unconditionally on mount off a
 * route param (`namespace`/`entity`), which is exactly `useQuery`'s
 * `enabled`-gated-on-param shape already established by `useBanner.js`
 * (`client/src/modules/banner/useBanner.js`) — the only other module in this
 * codebase doing a plain fetch-on-mount today.
 *
 * All requests hit the *external* Astrea backend
 * (`AuthService.getUrlAstreaPath()` in the legacy service), not this repo's
 * own `server_app` — confirmed via `grep -rn "migration" server_app/ -il`
 * returning no route matches, only an unrelated spec-path comment in
 * `userRouter.js`. Same cross-origin pattern already established by
 * `useHistoriesQuery`/`useClippingsQuery`/`useInactivatorMutations`: pass the
 * absolute Astrea URL (`getUrlAstreaPath(getUrlAstrea())`, the already-ported
 * equivalent of the legacy switch) to `httpClient` so axios uses it as-is
 * instead of prefixing its own configured `baseURL`, while `httpClient`'s
 * interceptors (fixed servlet Basic Auth + session header) still apply
 * regardless of target host.
 *
 * Legacy `MigrationService.getMigrations(namespace, index, onSuccess, onError)`:
 *
 *   migrationService.getMigrations = function(namespace, index, onSuccess, onError) {
 *     return $http.get(`${urlAstreaPath}/api/v2/migration/namespace/${namespace}/detailed`)
 *       .then(resp => {
 *         const countPromises = resp.data.migrationEntityStatusValues.map(status =>
 *           getMigrationStatusCount(namespace, status))
 *         return Promise.all(countPromises).then(counts => {
 *           counts.forEach(({status, count}) => {
 *             resp.data.total += count
 *             resp.data[status.toLowerCase()] = count
 *           })
 *           if (index != null) migrationService.migrations[index] = resp.data
 *           else migrationService.migrations = migrationService.migrations.concat(resp.data)
 *           onSuccess(true)
 *         })
 *       }).catch(onError)
 *   }
 *
 * i.e. one `detailed` fetch per namespace, fanned out into one
 * `count/status/:status` request per status name the backend reports via
 * `migrationEntityStatusValues`, then merged back onto the same record as
 * `total` (accumulated) and one lowercase-keyed field per status (`ready`,
 * `inprogress`, `done`, `error`, ...) — matched by the legacy template via
 * `migration.ready`, `migration.inProgress`, `migration.done`,
 * `migration.error`, `migration.invalid`, `migration.inNamespace`.
 *
 * `index` selects "replace the migration at this position" (used by the
 * per-row refresh button, `checkStatus(migration.namespace, $index)`) vs.
 * "append a new one" (the main search form, `checkStatus(namespace)` with no
 * index) — ported 1:1 as an explicit `index` argument to `fetchMigration`
 * below, with the *page* component (not this hook) owning the array so it
 * can splice/append exactly like the legacy service's shared
 * `migrations` array did.
 */
async function fetchMigrationStatusCount({ baseUrl, namespace, status }) {
  const response = await httpClient.get(
    `${baseUrl}/api/v2/migration/namespace/${namespace}/count/status/${status}`,
  )
  return response.data
}

async function fetchMigration({ baseUrl, namespace }) {
  const response = await httpClient.get(`${baseUrl}/api/v2/migration/namespace/${namespace}/detailed`)
  const migration = response.data

  const statusValues = migration.migrationEntityStatusValues || []
  const counts = await Promise.all(
    statusValues.map((status) => fetchMigrationStatusCount({ baseUrl, namespace, status })),
  )

  let total = migration.total || 0
  const countsByStatus = {}
  counts.forEach((entityCount) => {
    total += entityCount.count
    countsByStatus[entityCount.status.toLowerCase()] = entityCount.count
  })

  return { ...migration, ...countsByStatus, total }
}

/** Port of `MigrationService.getMigrations`, minus the shared-array bookkeeping (owned by the page). */
export function useMigrationSearchMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ namespace }) => {
      return fetchMigration({ baseUrl, namespace })
    },
  })
}

/**
 * Port of `MigrationService.deleteMigration(namespace, callback)`:
 *
 *   migrationService.deleteMigration = function(namespace, callback) {
 *     $http.get(urlAstrea + '/migration/deletemigrationentities?namespace=' + namespace)
 *       .then(data => callback && callback(data))
 *       .catch(...)
 *   }
 *
 * Legacy `confirm(...)` gate (native browser dialog) lives in the controller,
 * not the service — ported to a MUI `Dialog` in `MigrationListPage.jsx`
 * (see that file's doc-comment for why: no other legacy `window.confirm()`
 * call has been ported yet, `BannerPage.jsx`'s upload/remove confirmation
 * dialogs are the closest precedent for "native confirm -> MUI Dialog").
 */
export function useDeleteMigrationMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (namespace) => {
      const response = await httpClient.get(
        `${baseUrl}/migration/deletemigrationentities?namespace=${encodeURIComponent(namespace)}`,
      )
      return response.data
    },
  })
}

/**
 * Port of `MigrationService.fireMigration(namespace, callback)`:
 *
 *   migrationService.fireMigration = function(namespace, callback) {
 *     $http.get(urlAstrea + '/migration/startMigration?local=false&namespace=' + namespace)
 *       .then(data => callback && callback(true, data))
 *       .catch(data => callback && console.log('error', data))
 *   }
 *
 * The legacy controller only fires this when `migration.status === 'READY'`
 * (`ng-disabled="migration.status != 'READY'"` on the trigger button) —
 * ported as a guard in `MigrationListPage.jsx`, not duplicated here, so this
 * hook stays a plain unconditional mutation like its siblings.
 */
export function useFireMigrationMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (namespace) => {
      const response = await httpClient.get(
        `${baseUrl}/migration/startMigration?local=false&namespace=${encodeURIComponent(namespace)}`,
      )
      return response.data
    },
  })
}

/**
 * Port of `MigrationService.getMigrationDetail(namespace, callback)`:
 *
 *   migrationService.getMigrationDetail = function(namespace, callback) {
 *     $http.get(urlAstrea + '/migration/listmigrationproblems?namespace=' + namespace)
 *       .then(response => callback && callback(response.data))
 *       .catch(response => console.log("ERRRROR: " + response))
 *   }
 *
 * Backs `MigrationDetailPage.jsx` (`workspace.migration-details` /
 * `/workspace/migration/:namespace`): fetches the list of invalid entities
 * for a namespace, fired unconditionally on mount in the legacy controller
 * (`data-ng-init="initView()"`) — reproduced here as a plain `useQuery`
 * enabled whenever a namespace is present, so the page component doesn't
 * need to trigger it manually.
 */
export function useMigrationDetailQuery(namespace) {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useQuery({
    queryKey: ['migration-detail', namespace],
    queryFn: async () => {
      const response = await httpClient.get(
        `${baseUrl}/migration/listmigrationproblems?namespace=${encodeURIComponent(namespace)}`,
      )
      return response.data
    },
    enabled: !!namespace,
  })
}

/**
 * Port of `MigrationService.getMigrationEntityJson(migrationEntityId, callback)`:
 *
 *   migrationService.getMigrationEntityJson = function(migrationEntityId, callback) {
 *     $http.get(urlAstrea + '/migration/getMigrationEntityJson?migrationEntityId=' + migrationEntityId)
 *       .then(response => callback && callback(response.data))
 *       .catch(data => console.log('error', data))
 *   }
 *
 * Backs `MigrationJsonPage.jsx` (`workspace.migration-json` /
 * `/workspace/migration/json/:entity`): fetches the raw JSON text for one
 * migration entity, fired unconditionally on mount in the legacy controller
 * exactly like `getMigrationDetail` above. The legacy callback wrote the
 * response straight into `#jsonTextArea`'s value
 * (`$('#jsonTextArea').val(response.data)`) without parsing it — `response.data`
 * is already the raw JSON *text*, not a parsed object — so this hook returns
 * it unmodified for the page to put into a controlled textarea.
 */
export function useMigrationEntityJsonQuery(migrationEntityId) {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useQuery({
    queryKey: ['migration-entity-json', migrationEntityId],
    queryFn: async () => {
      const response = await httpClient.get(
        `${baseUrl}/migration/getMigrationEntityJson?migrationEntityId=${encodeURIComponent(migrationEntityId)}`,
      )
      return response.data
    },
    enabled: !!migrationEntityId,
  })
}

/**
 * Port of `MigrationService.editJson(json, idMigration, callback)`:
 *
 *   migrationService.editJson = function(json, idMigration, callback) {
 *     $http.post(urlAstrea + '/api/v2/migration/update', {json, idMigration})
 *       .then(data => callback && callback())
 *       .catch(data => console.log('error', data))
 *   }
 *
 * Backs `MigrationJsonPage.jsx`'s "Salvar" button
 * (`migrationJsonController.save`). Takes `{ json, migrationEntityId }` to
 * match this codebase's usual object-argument `mutate(...)` shape (see
 * `useMigrationSearchMutation` above) instead of positional arguments.
 */
export function useEditMigrationJsonMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ json, migrationEntityId }) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/migration/update`, {
        json,
        idMigration: migrationEntityId,
      })
      return response.data
    },
  })
}
